import { z } from 'zod';
import { itineraryPaymentSchema } from './itinerary';

// Match the Drizzle enums at packages/db/src/schema/index.ts:775-786 verbatim.
export const paymentLogStatusSchema = z.enum([
  'pending',
  'completed',
  'expired',
]);
export type PaymentLogStatus = z.infer<typeof paymentLogStatusSchema>;

export const paymentLogBillingTypeSchema = z.enum(['fixed', 'percentage']);
export type PaymentLogBillingType = z.infer<typeof paymentLogBillingTypeSchema>;

export const paymentLogMethodSchema = z.enum(['stripe', 'other']);
export type PaymentLogMethod = z.infer<typeof paymentLogMethodSchema>;

// Shared by the FE (display-only "Result (USD)") and the BE (authoritative
// charge computation). Ported from legacy PAYMENT_LOG_MARGIN (2% markup) and
// Stripe's documented $0.50 minimum charge.
export const PAYMENT_LOG_MARGIN = 1.02;
export const MIN_STRIPE_CHARGE_USD = 0.5;

// The margin covers the cost of converting the itinerary's currency into the
// USD that Stripe charges in. A USD itinerary is never converted, so no margin
// applies — charging it would silently inflate what the customer pays.
export const marginForCurrency = (
  currency: string | null | undefined
): number =>
  (currency ?? '').toUpperCase() === 'USD' ? 1 : PAYMENT_LOG_MARGIN;

const paymentLogItineraryRefSchema = z.object({
  id: z.uuid(),
  title: z.string().nullable(),
  itineraryRef: z.string().nullable(),
  organizationId: z.uuid(),
  activeVersion: z
    .object({
      id: z.uuid(),
      accountingReference: z.string().nullable(),
      price: z.string().nullable(),
    })
    .nullable(),
});

const paymentLogCustomerRefSchema = z.object({
  id: z.uuid(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  companyName: z.string().nullable(),
});

// Extends the existing itineraryPaymentSchema rather than forking it so the
// per-itinerary list (`itineraryPayments.list`) and the global finance list
// (`paymentLogs.findAll`) stay structurally compatible. checkoutId,
// checkoutUrl, and extra are present on the Drizzle table but absent from
// itineraryPaymentSchema — the global view needs them (Excel export reads
// row.checkoutUrl, percentage formula reads extra.priceCcy).
export const paymentLogItemSchema = itineraryPaymentSchema.extend({
  checkoutId: z.string().nullable(),
  checkoutUrl: z.string().nullable(),
  extra: z.unknown().nullable(),
  itinerary: paymentLogItineraryRefSchema.nullable(),
  customer: paymentLogCustomerRefSchema.nullable(),
});
export type PaymentLogItem = z.infer<typeof paymentLogItemSchema>;

export const findPaymentLogsInput = z.object({
  offset: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(500).optional(),
  search: z.string().min(1).optional(),
  statuses: z.array(paymentLogStatusSchema).optional(),
  billingTypes: z.array(paymentLogBillingTypeSchema).optional(),
  customerIds: z.array(z.uuid()).optional(),
  itineraryIds: z.array(z.uuid()).optional(),
  accountId: z.uuid().optional(),
  organizationIds: z.array(z.uuid()).optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  createdStartDate: z.string().optional(),
  createdEndDate: z.string().optional(),
});
export type FindPaymentLogsInput = z.infer<typeof findPaymentLogsInput>;

export const findPaymentLogsResultSchema = z.object({
  data: z.array(paymentLogItemSchema),
  count: z.number().int().nonnegative(),
  countAll: z.number().int().nonnegative(),
});
export type FindPaymentLogsResult = z.infer<typeof findPaymentLogsResultSchema>;

export const findPaymentLogInput = z.object({ id: z.uuid() });
export type FindPaymentLogInput = z.infer<typeof findPaymentLogInput>;

export const paymentLogFilterCountsResultSchema = z.object({
  itineraries: z.array(
    z.object({
      id: z.uuid(),
      title: z.string().nullable(),
      count: z.number().int().nonnegative(),
    })
  ),
  customers: z.array(
    z.object({
      id: z.uuid(),
      title: z.string().nullable(),
      count: z.number().int().nonnegative(),
    })
  ),
});
export type PaymentLogFilterCountsResult = z.infer<
  typeof paymentLogFilterCountsResultSchema
>;

const moneyString = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === 'number' ? v.toString() : v))
  .refine((v) => /^-?\d+(\.\d{1,2})?$/.test(v), {
    message: 'amount must be a decimal with up to 2 decimal places',
  });

export const updatePaymentLogInput = z
  .object({
    id: z.uuid(),
    status: paymentLogStatusSchema.optional(),
    method: paymentLogMethodSchema.optional(),
    billingType: paymentLogBillingTypeSchema.optional(),
    billingAmount: moneyString.optional(),
    billingAmountEur: moneyString.nullable().optional(),
    billingAmountUsd: moneyString.nullable().optional(),
    accountingReference: z.string().nullable().optional(),
    cardLast4: z.string().max(4).nullable().optional(),
    internalNote: z.string().nullable().optional(),
    paidOn: z.string().nullable().optional(),
  })
  .refine((v) => Object.keys(v).filter((k) => k !== 'id').length > 0, {
    message: 'At least one patch field must be provided',
  });
export type UpdatePaymentLogInput = z.infer<typeof updatePaymentLogInput>;

export const exportPaymentLogsResultSchema = z.object({
  base64: z.string(),
  filename: z.string(),
  mime: z.literal(
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ),
});
export type ExportPaymentLogsResult = z.infer<
  typeof exportPaymentLogsResultSchema
>;

export const paymentLinkTemplateResultSchema = z.object({
  template: z.string().nullable(),
});
export type PaymentLinkTemplateResult = z.infer<
  typeof paymentLinkTemplateResultSchema
>;

// --- Payment creation (itineraryPayments.create) -------------------------

export const sendPaymentLinkEmailInput = z.object({
  emails: z.array(z.email()).min(1),
  subject: z.string().default('Stripe Payment Link'),
  content: z.string(),
});
export type SendPaymentLinkEmailInput = z.infer<
  typeof sendPaymentLinkEmailInput
>;

// Mirrors the legacy CreateItineraryPaymentDto. `billingResult` (the USD
// charge) is intentionally NOT accepted from the client — the server
// recomputes it from the itinerary price + exchange rate + margin so the
// caller cannot dictate the amount charged.
export const createItineraryPaymentInput = z
  .object({
    itineraryId: z.uuid(),
    customerIds: z.array(z.uuid()).min(1),
    optigest: z.string().min(1),
    billingType: paymentLogBillingTypeSchema,
    billingAmount: z.number().positive(),
    // Optional: when omitted the server falls back to the active version's
    // payment_zone_id (set during pricing configuration).
    paymentZoneId: z.uuid().optional(),
    isExtra: z.boolean().default(false),
    reminder: z.boolean().default(false),
    reminderDate: z.string().optional(),
    internalNote: z.string().nullable().optional(),
    emailNotification: sendPaymentLinkEmailInput.optional(),
  })
  .superRefine((v, ctx) => {
    // UX-parity validation; the server is still the authority on the charge.
    if (v.isExtra) return;
    if (v.billingType === 'percentage' && v.billingAmount > 100) {
      ctx.addIssue({
        code: 'custom',
        path: ['billingAmount'],
        message: 'Percentage billing amount cannot exceed 100',
      });
    }
  });
export type CreateItineraryPaymentInput = z.infer<
  typeof createItineraryPaymentInput
>;

export const createItineraryPaymentResultSchema = z.array(
  z.object({
    paymentLogId: z.uuid(),
    customerName: z.string().nullable(),
    checkoutUrl: z.string(),
  })
);
export type CreateItineraryPaymentResult = z.infer<
  typeof createItineraryPaymentResultSchema
>;

// --- Invalidate an unpaid link (itineraryPayments.invalidate) -----------

export const invalidateItineraryPaymentInput = z.object({
  paymentLogId: z.uuid(),
});
export type InvalidateItineraryPaymentInput = z.infer<
  typeof invalidateItineraryPaymentInput
>;

export const invalidateItineraryPaymentResultSchema = z.object({
  paymentLogId: z.uuid(),
  status: paymentLogStatusSchema,
});
export type InvalidateItineraryPaymentResult = z.infer<
  typeof invalidateItineraryPaymentResultSchema
>;

// --- Customer checkout (public; payment-log id is the bearer credential) --

export const getCheckoutInput = z.object({ paymentLogId: z.uuid() });
export type GetCheckoutInput = z.infer<typeof getCheckoutInput>;

export const checkoutSessionResultSchema = z.object({
  clientSecret: z.string(),
  publishableKey: z.string(),
  itineraryId: z.uuid(),
  itineraryTitle: z.string().nullable(),
  itineraryRef: z.string().nullable(),
  amountUsd: z.number(),
  status: paymentLogStatusSchema,
});
export type CheckoutSessionResult = z.infer<typeof checkoutSessionResultSchema>;
